import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import Icon from 'components/Icons/Icon';
import Feedback from './Feedback';
import Alert from 'components/Common/Alert';
import './Section.scss';
import { generateRandomId } from 'components/Common/HelperFunctions/generateRandomId';
import FormSelect from './FormSelect';
import conxtdOut from 'apis/conxtdOut';
import CustomToggle from 'components/Common/CustomToggle/CustomToggle';
import { Tooltip } from 'components/Common/Tooltip';

function Section({
  title = "",
  altTitle = "",
  handleSetState,
  onChangeMethods,
  handleNullChange, 
  nullChangeArg, 
  required = false,
  containerStyles = "", 
  inputWrapperStyles = "",
  inputStyles = "",
  inlineWrapperStyles = {},
  type = "",
  defaultChecked = false,
  checkboxSize = "md",
  checkInvalid = false,
  setInvalidConditions = (() => { return }),
  handleInvalid,
  setFormData,
  triggerInitialiseForm,
  disableDefaultStyles = false,
  ariaDescription = '',
  prefix = '',
  label = true,
  deleteEnabled = false,
  deleteMessage = '',
  placeholder = '',
  valueType = 'string',
  initialValueProp = '',
  stateId = null,
  onDelete = null,
  isMulti = false,
  mapFromEndpoint = { endpoint: '', dispatch: null },
  mapFromObject = null,
  toggleLeftlabel = false,
  toggleRightLabel = false,
  toggleLeftValue = null,
  toggleRightValue = null,
  characterLimit = null,
}) {

  const darkMode = useSelector(state => state.app.darkMode);

  const randomId = uuidv4();

  // sets initial state to empty: array, object, or string, based on valueType prop provided (if set)
  const assignInitialValue = useCallback(() => {
    const type = initialValueProp ? typeof initialValueProp : valueType;
    return type === 'string' ? '' :
    type === 'array' ? [] :
    type === 'object' ? {} :
    type === 'file' ? null :
    type === 'bool' ? false : '';
  }, [initialValueProp, valueType])
  
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(assignInitialValue());
  }, [assignInitialValue])

  const handleSetValue = useCallback(({ argValue = assignInitialValue(), initialise = false } = {}) => {
    // value type is Array
    if (valueType === 'array') {
      setValue(initialise ? [] : argValue);
      return;
    }
    // value type is Object
    // requires stateId prop value as key for new object entries
    if (valueType === 'object') {
      if (!stateId) { return };
      setValue(prev => initialise ? {} : { ...prev, [stateId]: argValue ?? ''})
      return;
    }
    // value type is String
    if (valueType === 'string') {
      setValue(() => initialise ? '' : argValue)
      return;
    }
    
    // value type is File
    if (valueType === 'file') {
      setValue(() => initialise ? null : argValue)
      return;
    }
    
    // value type is Bool
    if (valueType === 'bool') {
      setValue(() => initialise ? false : argValue)
      return;
    }
  }, [assignInitialValue, valueType, stateId])

  const [sectionId] = useState(generateRandomId());
  const [photoFile, setPhotoFile] = useState(null);
  const [photoFileName, setPhotoFileName] = useState("");
  const [photoFileInvalid, setPhotoFileInvalid] = useState(false);
  const [dynamicInputKey, setDynamicInputKey] = useState(uuidv4());
  const [customContainerStyle, setCustomContainerStyle] = useState("");
  const [showCharacterTooltip, setShowCharacterTooltip] = useState(false);

  const handleDeleteSection = () => {

    if (!stateId || !onDelete || !deleteEnabled) { return; }
    
    Alert({
      html: `<div>Are you sure you want to delete ${deleteMessage ?? 'this'}?</div>`,
      showCancelButton: true,
      confirmButtonText: `Delete`,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        onDelete?.(stateId);
      } else {
        return;
      }
    })
  }

  useEffect(() => {
    if (triggerInitialiseForm) {
      handleSetValue({ argValue: assignInitialValue(), initialise: true });
      setPhotoFile(null);
      setPhotoFileName('');
      setPhotoFileInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerInitialiseForm, assignInitialValue])

  // sets invalid condition for required value if null
  useEffect(() => {
    if (checkInvalid) {
      const isFileInvalid = photoFileInvalid !== false;
      
      setInvalidConditions(prev => ({
        ...prev,
        [title]: {
          condition:
            isFileInvalid ||
            (required && (
              value === '' ||
              value === null ||
              value === false ||
              value === undefined
            )),
          message: {
            title,
            message: isFileInvalid ? 
              // checks if value is invalid file -
              typeof(photoFileInvalid) !== String ? 
                'format is invalid' 
              : 
                photoFileInvalid
                // - or null string
            : 'is required',
            id: randomId 
          }
        }
      }));
    }

    // adds value to form data in parent component (or other state)
    handleSetState?.(value);
    setFormData(prev => {  
      if (!title) return prev;

      const modifiedTitle = title?.toLowerCase()?.replaceAll(' ', '_');
      
      if (valueType === 'object' && stateId) {
        return {
          ...prev,
          [modifiedTitle]: {
            ...(prev[modifiedTitle] ?? {}),
            [stateId]: value?.[stateId],
          },
        };
      }
      
      return {
        ...prev,
        [modifiedTitle]: value,
      }
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, photoFileInvalid, photoFile, checkInvalid, required])

  const onFileChange = (e) => {
    setPhotoFile(e.target.files[0]);
    setPhotoFileName(e.target.files?.[0]?.name);
  };

  useEffect(() => {
    if (photoFile) {
      if (photoFile?.size > 2 * 1024 * 1024) {
        setPhotoFileInvalid("File is too large")
        return;
      } else if (!["image/png", "image/jpeg"].includes(photoFile?.type)) {
        setPhotoFileInvalid(
          <>
            File is not an image format that we accept. Suggested formats are:
            <span className="font-italic font-weight-bold ml-1">jpeg, png</span>
          </>
        )
        return;
      } else {
        setPhotoFileInvalid(false);
        handleSetValue({ argValue: photoFile, initialise: false });
      }
    } else {
      setPhotoFileInvalid(false);
      setDynamicInputKey(uuidv4());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoFile])

  useEffect (() => {
    if (type === "image") {
      handleInvalid?.(photoFileInvalid);
    }
  }, [type, photoFileInvalid, handleInvalid])

  const image = () => {
    return (
      <span>
        <Form.Label htmlFor="photo-file-input">{`${title} ${required ? '*' : ''}`}</Form.Label>
        <div className="custom-file mb-2 d-flex justify-content-between">
          <span className='col w-100'>
            <input
              key={dynamicInputKey}
              type="file"
              className="custom-file-input"
              id="photo-file-input"
              onChange={onFileChange}
            />
            <label className="custom-file-label" htmlFor="customFile">
              {photoFileName ?? ""}
            </label>
          </span>

          {/* remove inputted-file button */}
          <Button
            className="col file-clear-button"
            style={{
              maxWidth: photoFile ? '18px' : '0px',
              opacity: photoFile ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-width 0.1s ease-in-out, opacity 0.5s ease-in-out, background-color 0.2s, color 0.2s',
              padding: photoFile ? '' : '0',
              border: photoFile ? '' : 'none',
              margin: photoFile ? '' : '0',
            }}
            onClick={() => {
              setPhotoFile(null);
              setPhotoFileName('');
              handleSetValue({ argValue: assignInitialValue(), initialise: true });
            }}
          >
            <Icon className='fas fa-times' />
          </Button>
        </div>

        <Feedback 
          condition={photoFileInvalid}
          messages={photoFileInvalid ? [photoFileInvalid] : []}
        />
      </span>
    )
  }
  
  const checkbox = () => {

    !customContainerStyle && setCustomContainerStyle('d-flex flex-row align-items-center justify-content-between flex-wrap');

    return (
      <>
        <Form.Label className='checkbox-label' htmlFor={`${title}_checkbox`}>{`${title} ${required ? '*' : ''}`}</Form.Label>
        <span className='connecting-line'></span>
        <div className={`${checkboxSize} d-flex align-items-center checkbox`}>
          <Form.Control
            data-intercom-target={title}
            type='checkbox'
            defaultChecked={defaultChecked}
            name={`${title}_checkbox`}
            id={`${title}_checkbox`}
            className={`${inputStyles} checkbox-control`}
            value={valueType === 'object' && stateId ? value?.[stateId] : value}
            onChange={(e) => {
              handleSetValue({ argValue: e.target.checked ?? '', initialise: false });
              handleSetState?.(e);
              handleNullChange?.({nullChangeArg}, e.target.value);
              checkInvalid && handleInvalid?.(e);
              onChangeMethods?.(e);
            }}
          />
        </div>
      </>
    )
  }
  
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(isMulti ? [] : null);
  
  useEffect(() => {
    handleSetValue({ 
      argValue: isMulti ? selectedOption?.map(o => o.value) : selectedOption?.value, 
      initialise: false,
    })
  }, [selectedOption, isMulti, handleSetValue])

    useEffect(() => {
      const { endpoint } = mapFromEndpoint;
      if (endpoint) {
        const options = conxtdOut.get(endpoint);
        options.then((res) => {
          const data = Object.values(res?.data)?.[0];
          if (data) {
            const formattedData = Object.entries(data).map(([label, value]) => {
              return { label, value };
            });
            setDropdownOptions(formattedData);
          }
        }).catch((error) => {
          console.log('Error retrieving inAlarmTypes: ', error)
        })
      } else if (mapFromObject) {
        setDropdownOptions(Object.entries(mapFromObject)?.map(([key, value]) => ({ label: key, value })));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(mapFromEndpoint, mapFromObject)])

  const dropdown = () => {

    return (
      <div style={inlineWrapperStyles}>
        <FormSelect
          title={altTitle || title}
          options={dropdownOptions}
          placeholder={placeholder}
          onChangeMethods={(e) => setSelectedOption(e)}
          value={selectedOption ?? null}
          isMulti={isMulti}
        />
      </div>
    )
  }

  const toggle = () => {

    return (
      <div style={inlineWrapperStyles}>
        {toggleLeftlabel && <Form.Label htmlFor={`${title}_toggle`}>{`${toggleLeftlabel} ${required ? '*' : ''}`}</Form.Label>}
        <Form.Check
          type="switch"
          id={`${title}_toggle`}
          label={toggleLeftlabel || toggleRightLabel ? '' : `${title} ${required ? '*' : ''}`}
          data-intercom-target={title}
          className={`${inputStyles} toggle-control`}
          onChange={(e) => {
            handleSetValue({ argValue: e.target.checked ?? '', initialise: false });
            handleSetState?.(e);
            handleNullChange?.({nullChangeArg}, e.target.value);
            checkInvalid && handleInvalid?.(e);
            onChangeMethods?.(e);
          }}
        />
        {toggleRightLabel && <Form.Label htmlFor={`${title}_toggle`}>{`${toggleRightLabel} ${required ? '*' : ''}`}</Form.Label>}
      </div>
    )
  }

  const handleToggleSetValue = useCallback((selectedValue) => {
    handleSetValue({ argValue: selectedValue, initialise: false });
  }, [handleSetValue]);

  useEffect(() => {
    // Initialise the form value to leftValue on mount
    if (toggleLeftValue) {
      handleSetValue({ argValue: toggleLeftValue, initialise: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleLeftValue]);
  
  const customToggle = () => {

    return (
      <CustomToggle
        name={title}
        leftLabel={toggleLeftlabel}
        rightLabel={toggleRightLabel}
        leftValue={toggleLeftValue}
        rightValue={toggleRightValue}
        defaultValue={toggleLeftValue}
        handleSetState={handleToggleSetValue}
      />
    )
  }

  // selects the component type if a type prop is provided ->
  const renderOptions = (i) => {
    const options = { checkbox, image, dropdown, toggle, customToggle };
    return options[i]?.();
  };
  // -------------------------------------------------------|

  return (
    <div
      key={`${title}_${type}_element`}
      className={
        `
          ${disableDefaultStyles ? '' : 'custom-form-section'}
          ${!type ? 'standard' : ''}
          ${(customContainerStyle || containerStyles) ?? ""}
          w-100
        `
      }
    >
      {type ? renderOptions(type) :
        <>
          {label && <Form.Label>{`${title} ${required ? '*' : ''}`}</Form.Label>}
          <div className={`${inputWrapperStyles} ${type}`}>
            
            <InputGroup className='my-0'>
              {/* dynamic prefix */}
              {prefix ?
                <InputGroup.Append>
                  <InputGroup.Text style={{ fontSize: '0.9em', borderTopLeftRadius: '5px', borderBottomLeftRadius: '5px' }}>{prefix}</InputGroup.Text>
                </InputGroup.Append>
              : 
                null
              }
              
              {/* standard text input */}
              {showCharacterTooltip ?
                <Tooltip
                  description={`There is a character limit of ${characterLimit} on this field`}
                  placement='top'
                  white={darkMode ? false : true}
                >
                  <Form.Control
                    id={sectionId}
                    data-intercom-target={title}
                    name={title}
                    className={`${inputStyles} ${deleteEnabled ? 'col' : ''}`}
                    value={valueType === 'object' && stateId ? value?.[stateId] : value}
                    aria-describedby={sectionId}
                    placeholder={placeholder}
                    onChange={(e) => {
                      if (characterLimit && e.target.value.length > characterLimit) {
                        setShowCharacterTooltip(true);
                        return;
                      } else {
                        setShowCharacterTooltip(false);
                      }
                      handleSetValue({ argValue: e.target.value ?? '', initialise: false });
                      handleSetState?.(e);
                      handleNullChange?.({ nullChangeArg }, e.target.value);
                      onChangeMethods?.(e);
                    }}
                  />
                </Tooltip>
              :
                <Form.Control
                  id={sectionId}
                  data-intercom-target={title}
                  name={title}
                  className={`${inputStyles} ${deleteEnabled ? 'col' : ''}`}
                  value={valueType === 'object' && stateId ? value?.[stateId] : value}
                  aria-describedby={sectionId}
                  placeholder={placeholder}
                  onChange={(e) => {
                    if (characterLimit && e.target.value.length > characterLimit) {
                      setShowCharacterTooltip(true);
                      return;
                    } else {
                      setShowCharacterTooltip(false);
                    }
                    handleSetValue({ argValue: e.target.value ?? '', initialise: false });
                    handleSetState?.(e);
                    handleNullChange?.({ nullChangeArg }, e.target.value);
                    onChangeMethods?.(e);
                  }}
                />
              }
              {deleteEnabled && 
                <Button
                  variant='outline-danger'
                  className=''
                  onClick={() => handleDeleteSection()}
                  style={{
                    borderTopLeftRadius: '0px',
                    borderBottomLeftRadius: '0px',
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                  }}
                >
                <Icon 
                  className='fas fa-trash'
                />
              </Button>
              }
            </InputGroup>

            {ariaDescription && (
              <Form.Text id={sectionId} muted>
                {ariaDescription}
              </Form.Text>
            )}
          </div>
        </>
      }
    </div>
  )
}

export default Section;
